import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  filter,
  map,
  switchMap,
  take,
} from 'rxjs/operators';

export type SiteVisitPdfPollStatus = 'IN_PROGRESS' | 'SUCCESS' | 'FAILED';

export interface SiteVisitPdfPollResult {
  success: boolean;
  status: SiteVisitPdfPollStatus;
  pdfId?: string;
  downloadUrl?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SiteVisitPdfService {
  constructor(private httpClient: HttpClient) {}

  extractPdfJobId(response: unknown): string | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const obj = response as Record<string, unknown>;
    const nested =
      obj['data'] && typeof obj['data'] === 'object'
        ? (obj['data'] as Record<string, unknown>)
        : null;
    const candidates = [obj['pdfId'], nested?.['pdfId']];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return null;
  }

  extractReportPdfUrl(response: unknown): string | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const obj = response as Record<string, unknown>;
    const nested =
      obj['data'] && typeof obj['data'] === 'object'
        ? (obj['data'] as Record<string, unknown>)
        : null;

    return (
      this.extractDownloadUrl(obj) ??
      (nested ? this.extractDownloadUrl(nested) : undefined) ??
      null
    );
  }

  normalizePdfPollResult(raw: unknown, pdfId: string): SiteVisitPdfPollResult {
    const obj =
      raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const nested =
      obj['data'] && typeof obj['data'] === 'object'
        ? (obj['data'] as Record<string, unknown>)
        : null;
    const status = String(obj['status'] ?? nested?.['status'] ?? 'IN_PROGRESS').toUpperCase();
    const downloadUrl = this.extractDownloadUrl(obj) ?? (nested ? this.extractDownloadUrl(nested) : undefined);

    if (status === 'SUCCESS') {
      return {
        success: Boolean(obj['success'] ?? true),
        status: 'SUCCESS',
        pdfId: String(obj['pdfId'] ?? pdfId),
        downloadUrl,
        message:
          typeof obj['message'] === 'string' ? obj['message'] : undefined,
      };
    }

    if (status === 'FAILED') {
      return {
        success: false,
        status: 'FAILED',
        pdfId: String(obj['pdfId'] ?? pdfId),
        message:
          typeof obj['message'] === 'string'
            ? obj['message']
            : 'PDF generation failed.',
      };
    }

    return {
      success: Boolean(obj['success'] ?? true),
      status: 'IN_PROGRESS',
      pdfId: String(obj['pdfId'] ?? pdfId),
      message:
        typeof obj['message'] === 'string'
          ? obj['message']
          : 'PDF is being generated.',
    };
  }

  pollUntilDone(pdfId: string): Observable<SiteVisitPdfPollResult> {
    const maxAttempts = 20;
    const intervalMs = 10000;
    const statusUrl = `/jangrihasitevisit/site-visit/pdf-status/${encodeURIComponent(pdfId)}`;

    const timedOut: SiteVisitPdfPollResult = {
      success: false,
      status: 'FAILED',
      pdfId,
      message: 'PDF generation timed out. Please try again.',
    };

    return timer(0, intervalMs).pipe(
      take(maxAttempts),
      switchMap((): Observable<SiteVisitPdfPollResult> =>
        this.httpClient.get<unknown>(statusUrl).pipe(
          map((body) => this.normalizePdfPollResult(body, pdfId)),
          catchError(() =>
            of<SiteVisitPdfPollResult>({
              success: false,
              status: 'FAILED',
              pdfId,
              message: 'Could not check PDF status. Try again.',
            })
          )
        )
      ),
      filter(
        (result) =>
          result.status === 'SUCCESS' || result.status === 'FAILED'
      ),
      take(1),
      defaultIfEmpty(timedOut)
    );
  }

  /** Handles string, string[], or nested url objects from site-visit PDF APIs. */
  private extractDownloadUrl(obj: Record<string, unknown>): string | undefined {
    const candidates: unknown[] = [
      obj['downloadUrl'],
      obj['download_url'],
      obj['url'],
      obj['pdfUrl'],
      obj['pdf_url'],
      obj['get_url'],
    ];
    for (const candidate of candidates) {
      const url = this.pickUrlFromCandidate(candidate);
      if (url) {
        return url;
      }
    }
    return undefined;
  }

  private pickUrlFromCandidate(candidate: unknown): string | null {
    if (!candidate) {
      return null;
    }
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
    if (Array.isArray(candidate)) {
      for (const item of candidate) {
        const url = this.pickUrlFromCandidate(item);
        if (url) {
          return url;
        }
      }
      return null;
    }
    if (typeof candidate === 'object') {
      const obj = candidate as Record<string, unknown>;
      if (typeof obj['url'] === 'string' && obj['url'].trim()) {
        return obj['url'].trim();
      }
    }
    return null;
  }
}
